import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Send, Trash2, MessagesSquare, Maximize2, ArrowLeft } from 'lucide-react';
import liveChatService, {
  LiveChatEntitySegment,
  LiveChatMessage,
} from '../../services/liveChatService';
import socketService from '../../services/socketService';
import commentService from '../../services/commentService';
import { useAuth } from '../../contexts/AuthContext';

// ===================================================================
// LiveChat — generischer Gruppen-Live-Chat (2026-08-27).
// Wiederverwendbar für jeden Bereich: einfach
//   <LiveChat entityType="prediction-game" entityId={id} />
// einbauen (entityType = URL-Segment des Backends).
// Realtime über Socket.io + sanftes Polling als Fallback.
// ===================================================================

const TEAL = '#3fced1';
const POLL_INTERVAL = 30000; // Fallback, falls Socket-Events verloren gehen

interface Props {
  entityType: LiveChatEntitySegment;
  entityId?: string | null;
  /** Höhe der Nachrichtenliste (Standard: 480px); Strings wie '60vh' erlaubt */
  height?: number | string;
  /** Eigener Leerzustand-Text (Standard: "Noch keine Nachrichten — schreib die erste!") */
  emptyText?: string;
  /** true: auf Mobile (<640px) beim Einblenden direkt im Vollbild öffnen */
  autoFullscreenOnMobile?: boolean;
  /** true: startet direkt im Vollbild (Overlay-Modus) */
  initialFullscreen?: boolean;
  /** Overlay-Modus: Back schließt das Overlay über diesen Callback (statt in die Karten-Ansicht zu wechseln) */
  onFullscreenClose?: () => void;
  /**
   * Full-Bleed-Modus (Lobby-Chat auf der Startseite):
   * - `height` gilt für die GANZE Komponente (Wurzel = flex flex-col),
   *   Nachrichtenliste = flex-1, Composer als nicht-scrollender Footer
   *   (shrink-0) → Eingabefeld ist IMMER unten sichtbar
   * - auf Mobile (<640px) ohne Card-Optik (kein rounded/border/bg),
   *   ab sm: wie bisher als Card
   * Default false = exakt bisheriges Verhalten (Tippspiel-Seite unverändert:
   * `height` = Höhe der Nachrichtenliste, Card-Optik auf allen Breakpoints).
   */
  fullBleed?: boolean;
}

const formatTime = (ts?: number | null): string => {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

const formatDay = (ts: number): string => {
  const date = new Date(ts);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Heute';
  if (date.toDateString() === yesterday.toDateString()) return 'Gestern';
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const LiveChat: React.FC<Props> = ({ entityType, entityId, height = 480, emptyText, autoFullscreenOnMobile = false, initialFullscreen = false, onFullscreenClose, fullBleed = false }) => {
  const { user, isAuthenticated } = useAuth();
  const [messages, setMessages] = useState<LiveChatMessage[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(initialFullscreen);
  const listRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);

  const viewerId = user?.id ?? null;
  const isAdmin = String((user as any)?.role ?? '').toUpperCase() === 'ADMIN';

  // Neue Nachrichten dedupliziert einsortieren (Socket + Polling können sich überlappen)
  const mergeMessages = useCallback((incoming: LiveChatMessage[]) => {
    setMessages((prev) => {
      const known = new Set(prev.map((m) => m.id));
      const fresh = incoming.filter((m) => m.id && !known.has(m.id));
      if (fresh.length === 0) return prev;
      return [...prev, ...fresh].sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0));
    });
  }, []);

  const loadLatest = useCallback(async (initial: boolean) => {
    if (!entityId) return;
    try {
      const result = await liveChatService.getMessages(entityType, entityId, { limit: 50 });
      if (initial) {
        setMessages(result.messages);
        setHasMore(result.hasMore);
      } else {
        mergeMessages(result.messages);
      }
    } catch {
      /* Polling-Fehler still schlucken — Socket bleibt aktiv */
    } finally {
      if (initial) setLoading(false);
    }
  }, [entityType, entityId, mergeMessages]);

  // Initial-Load + Polling-Fallback
  useEffect(() => {
    if (!entityId) return;
    setLoading(true);
    loadLatest(true);
    const interval = window.setInterval(() => loadLatest(false), POLL_INTERVAL);
    return () => window.clearInterval(interval);
  }, [entityId, loadLatest]);

  // Realtime: Raum-Channel subscriben
  useEffect(() => {
    if (!entityId) return;
    const channel = liveChatService.buildChannelName(entityType, entityId);
    socketService.subscribe(channel);

    const offMessage = socketService.on<LiveChatMessage>('liveChatMessage', (msg) => {
      if (msg?.entityId === entityId) mergeMessages([msg]);
    });
    const offDeleted = socketService.on<{ id?: string; entityId?: string }>(
      'liveChatMessageDeleted',
      (payload) => {
        if (payload?.entityId === entityId && payload.id) {
          setMessages((prev) => prev.filter((m) => m.id !== payload.id));
        }
      },
    );

    return () => {
      offMessage();
      offDeleted();
      socketService.unsubscribe(channel);
    };
  }, [entityType, entityId, mergeMessages]);

  // Auf Mobile direkt im Vollbild starten (Tab-Klick = Chat öffnen)
  useEffect(() => {
    if (autoFullscreenOnMobile && window.innerWidth < 640) {
      setIsFullscreen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Im Fullscreen den Seiten-Scroll sperren
  useEffect(() => {
    if (!isFullscreen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isFullscreen]);

  // Auto-Scroll ans Ende, solange der Nutzer unten "klebt"
  useEffect(() => {
    const list = listRef.current;
    if (list && stickToBottomRef.current) {
      list.scrollTop = list.scrollHeight;
    }
  }, [messages, loading]);

  const handleScroll = () => {
    const list = listRef.current;
    if (!list) return;
    stickToBottomRef.current = list.scrollHeight - list.scrollTop - list.clientHeight < 80;
  };

  const loadOlder = async () => {
    if (!entityId || loadingOlder) return;
    const oldest = messages[0]?.createdAt;
    if (!oldest) return;
    setLoadingOlder(true);
    try {
      const result = await liveChatService.getMessages(entityType, entityId, {
        limit: 50,
        before: oldest,
      });
      setHasMore(result.hasMore);
      setMessages((prev) => {
        const known = new Set(prev.map((m) => m.id));
        const older = result.messages.filter((m) => m.id && !known.has(m.id));
        return [...older, ...prev];
      });
    } catch {
      /* ignorieren */
    } finally {
      setLoadingOlder(false);
    }
  };

  const handleSend = async () => {
    const content = input.trim();
    if (!content || !entityId || sending) return;
    setSending(true);
    setError(null);
    try {
      const msg = await liveChatService.postMessage(entityType, entityId, content);
      setInput('');
      if (msg) mergeMessages([msg]);
      stickToBottomRef.current = true;
    } catch (e: any) {
      const backendMessage = typeof e?.response?.data === 'string' ? e.response.data : null;
      setError(backendMessage || 'Nachricht konnte nicht gesendet werden');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (messageId: string) => {
    try {
      await liveChatService.deleteMessage(messageId);
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
    } catch {
      /* ignorieren */
    }
  };

  // Tagestrenner vorbereiten
  const rows = useMemo(() => {
    const result: Array<{ type: 'day'; key: string; label: string } | { type: 'msg'; key: string; msg: LiveChatMessage }> = [];
    let lastDay = '';
    messages.forEach((msg) => {
      const day = msg.createdAt ? new Date(msg.createdAt).toDateString() : '';
      if (day && day !== lastDay) {
        lastDay = day;
        result.push({ type: 'day', key: `day-${day}`, label: formatDay(msg.createdAt!) });
      }
      result.push({ type: 'msg', key: msg.id ?? `msg-${result.length}`, msg });
    });
    return result;
  }, [messages]);

  // Wurzel ist IMMER flex flex-col: Kopfzeile + Composer sind shrink-0-
  // Zeilen, die Nachrichtenliste scrollt dazwischen → der Composer kann
  // nie aus dem Chat herausgeschoben werden.
  //   - Card-Modus (Default): Liste hat die feste `height` (bisheriges
  //     Verhalten, Tippspiel-Seite unverändert)
  //   - fullBleed: `height` sitzt auf der Wurzel, Liste = flex-1; auf
  //     Mobile ohne Card-Optik, ab sm: Card wie bisher
  const chatUi = (
    <div
      className={
        isFullscreen
          ? 'fixed inset-0 z-[200] flex flex-col bg-zinc-950'
          : fullBleed
            ? 'flex min-h-0 flex-col overflow-hidden sm:rounded-2xl sm:border sm:border-zinc-800 sm:bg-zinc-900/60'
            : 'flex flex-col rounded-2xl border border-zinc-800 bg-zinc-900/60 overflow-hidden'
      }
      style={!isFullscreen && fullBleed ? { height } : undefined}
    >
      {isFullscreen ? (
        /* Vollbild-Kopfzeile wie in der App: Back-Pfeil + zentrierter Titel */
        <div className="flex h-14 shrink-0 items-center border-b border-zinc-800 px-2">
          <button
            onClick={() => (onFullscreenClose ? onFullscreenClose() : setIsFullscreen(false))}
            className="p-2.5 rounded-xl text-white hover:bg-zinc-800 transition"
            aria-label="Zurück"
          >
            <ArrowLeft size={22} />
          </button>
          <span className="flex-1 text-center text-base font-semibold text-white">Live-Chat</span>
          <div className="w-11" aria-hidden="true" />
        </div>
      ) : (
        /* Karten-Kopfzeile: Titel + Vollbild-Button (mobile) */
        <div className="flex shrink-0 items-center justify-between px-4 py-2.5 border-b border-zinc-800">
          <span className="text-sm font-semibold text-white">Live-Chat</span>
          <button
            onClick={() => setIsFullscreen(true)}
            className={`p-2 -m-1 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition ${fullBleed ? '' : 'sm:hidden'}`}
            aria-label="Vollbild"
          >
            <Maximize2 size={18} />
          </button>
        </div>
      )}

      {/* Nachrichtenliste */}
      <div
        ref={listRef}
        onScroll={handleScroll}
        className={
          isFullscreen || fullBleed
            ? 'min-h-0 flex-1 overflow-y-auto px-4 py-4'
            : 'overflow-y-auto px-4 py-4'
        }
        style={isFullscreen || fullBleed ? undefined : { height }}
      >
        {loading ? (
          <div className="h-full flex items-center justify-center text-zinc-500 text-sm">
            Chat wird geladen …
          </div>
        ) : messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-zinc-500">
            <MessagesSquare size={40} className="opacity-40" />
            <p className="text-sm">{emptyText ?? 'Noch keine Nachrichten — schreib die erste!'}</p>
          </div>
        ) : (
          <>
            {hasMore && (
              <div className="flex justify-center mb-4">
                <button
                  onClick={loadOlder}
                  disabled={loadingOlder}
                  className="px-4 py-1.5 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-medium transition disabled:opacity-60"
                >
                  {loadingOlder ? 'Lädt …' : 'Ältere Nachrichten laden'}
                </button>
              </div>
            )}
            <div className="space-y-3">
              {rows.map((row) => {
                if (row.type === 'day') {
                  return (
                    <div key={row.key} className="flex justify-center">
                      <span className="px-3 py-1 rounded-full bg-zinc-800 text-zinc-400 text-[11px] font-medium">
                        {row.label}
                      </span>
                    </div>
                  );
                }
                const msg = row.msg;
                const own = viewerId != null && msg.senderId === viewerId;
                const avatarUrl = commentService.buildAvatarUrl(msg.senderAvatarUrl, 100);
                return (
                  <div key={row.key} className={`group flex gap-2.5 ${own ? 'flex-row-reverse' : ''}`}>
                    <div className="shrink-0 w-8 h-8 rounded-full bg-zinc-700 overflow-hidden flex items-center justify-center">
                      {avatarUrl ? (
                        <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-[11px] font-bold text-zinc-300">
                          {(msg.senderName ?? '?').charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className={`max-w-[78%] ${own ? 'text-right' : ''}`}>
                      <div
                        className={
                          'inline-block px-3.5 py-2 rounded-2xl text-left ' +
                          (own
                            ? 'bg-[#3fced1]/15 border border-[#3fced1]/30'
                            : 'bg-zinc-800 border border-zinc-700/60')
                        }
                      >
                        <div className="text-[11px] font-semibold mb-0.5" style={{ color: TEAL }}>
                          {msg.senderName ?? 'Benutzer'}
                        </div>
                        <p className="text-sm text-zinc-100 whitespace-pre-wrap break-words leading-snug">
                          {msg.content}
                        </p>
                        <div className="mt-0.5 flex items-center justify-end gap-2">
                          {(own || isAdmin) && msg.id && (
                            <button
                              onClick={() => handleDelete(msg.id!)}
                              className="opacity-0 group-hover:opacity-100 transition text-zinc-500 hover:text-red-400"
                              aria-label="Nachricht löschen"
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                          <span className="text-[10px] text-zinc-500">{formatTime(msg.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Eingabe — nicht-scrollender Footer, IMMER unten sichtbar */}
      <div className="shrink-0 border-t border-zinc-800 p-3">
        {isAuthenticated ? (
          <>
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                rows={1}
                maxLength={500}
                placeholder="Nachricht schreiben …"
                className="flex-1 resize-none rounded-xl bg-zinc-800 border border-zinc-700 px-3.5 py-2.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-[#3fced1]/60"
              />
              <button
                onClick={handleSend}
                disabled={sending || !input.trim()}
                className="p-3 rounded-xl bg-[#3fced1] hover:bg-[#35b5b8] text-black transition disabled:opacity-50"
                aria-label="Senden"
              >
                <Send size={18} />
              </button>
            </div>
            {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
          </>
        ) : (
          <div className="flex items-center justify-between gap-3 px-1">
            <p className="text-sm text-zinc-400">Melde dich an, um mitzuschreiben.</p>
            <Link
              to="/login"
              className="shrink-0 px-4 py-2 rounded-xl bg-[#3fced1] hover:bg-[#35b5b8] text-black text-sm font-semibold transition"
            >
              Anmelden
            </Link>
          </div>
        )}
      </div>
    </div>
  );

  // Fullscreen als Portal direkt in den <body>: entkommt transformierten
  // Vorfahren, sonst liegt das "fixed"-Overlay NICHT über der Navbar
  return isFullscreen ? createPortal(chatUi, document.body) : chatUi;
};

export default LiveChat;
